import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { getProducts, addProduct, updateProduct, deleteProduct } from '../firebase/services';
import { collection, getDocs, deleteDoc, doc, addDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import productsData from '../data/productsData';
import AdminProductForm from '../components/AdminProductForm';
import CustomOrderManagement from './CustomOrderManagement';
import AllOrdersManagement from './AllOrdersManagement';
import { normalizeCategory } from '../utils/categories';
import { sendTestNotification } from '../services/telegramService';
import '../styles/AdminPanel.css';

const AdminPanel = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState(['All']);

  useEffect(() => {
    if (user && user.role === 'admin') {
      loadProducts();
    }
  }, [user]);

  const loadProducts = async () => {
    try {
      const productsData = await getProducts();
      setProducts(productsData);
      extractCategories(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractCategories = (productsData) => {
    const uniqueCategories = ['All', ...new Set(productsData.map(p => p.category).filter(Boolean))];
    setCategories(uniqueCategories);
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (!user || user.role !== 'admin') {
    return <Navigate to="/" />;
  }

  const handleSave = async (product) => {
    try {
      if (editingProduct) {
        await updateProduct(product.id, product);
      } else {
        await addProduct(product);
      }
      await loadProducts();
      setShowForm(false);
      setEditingProduct(null);
      alert('Product saved successfully!');
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product. Please try again.');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(id);
        await loadProducts();
        alert('Product deleted successfully!');
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Error deleting product. Please try again.');
      }
    }
  };

  const handleToggleAvailability = async (product) => {
    try {
      const newAvailability = !product.available;
      
      // Update local state immediately for instant feedback
      setProducts(prevProducts => 
        prevProducts.map(p => 
          p.id === product.id ? { ...p, available: newAvailability } : p
        )
      );
      
      // Update in database
      await updateProduct(product.id, {
        ...product,
        available: newAvailability
      });
    } catch (error) {
      console.error('Error toggling availability:', error);
      alert('Error updating availability. Please try again.');
      // Reload products to revert the optimistic update
      await loadProducts();
    }
  };

  const handleClearAllProducts = async () => {
    const confirmMessage = 'Are you sure you want to DELETE ALL PRODUCTS? This action cannot be undone!';
    
    if (window.confirm(confirmMessage)) {
      const doubleConfirm = window.confirm('This will permanently delete all products. Are you absolutely sure?');
      
      if (doubleConfirm) {
        try {
          setLoading(true);
          const productsRef = collection(db, 'products');
          const snapshot = await getDocs(productsRef);
          
          console.log(`Deleting ${snapshot.size} products...`);
          
          const deletePromises = [];
          snapshot.forEach((document) => {
            deletePromises.push(deleteDoc(doc(db, 'products', document.id)));
          });
          
          await Promise.all(deletePromises);
          
          await loadProducts();
          alert(`Successfully deleted ${snapshot.size} products!`);
        } catch (error) {
          console.error('Error clearing products:', error);
          alert('Error clearing products: ' + error.message);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleBulkImport = async () => {
    const confirmMessage = `This will add ${productsData.length} products to your catalog. Continue?`;
    
    if (window.confirm(confirmMessage)) {
      try {
        setLoading(true);
        const productsRef = collection(db, 'products');
        
        console.log(`Importing ${productsData.length} products...`);
        
        const importPromises = [];
        for (const product of productsData) {
          importPromises.push(addDoc(productsRef, {
            ...product,
            available: product.available !== false,
            createdAt: new Date()
          }));
        }
        
        await Promise.all(importPromises);
        
        await loadProducts();
        alert(`Successfully imported ${productsData.length} products!`);
      } catch (error) {
        console.error('Error importing products:', error);
        alert('Error importing products: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  const handleNormalizeCategories = async () => {
    if (!window.confirm('This will standardize all product categories. Continue?')) {
      return;
    }

    try {
      setLoading(true);
      let updated = 0;

      for (const product of products) {
        const normalizedCategory = normalizeCategory(product.category);
        if (product.category !== normalizedCategory) {
          await updateProduct(product.id, {
            ...product,
            category: normalizedCategory
          });
          updated++;
        }
      }

      await loadProducts();
      alert(`Category normalization complete! Updated ${updated} products.`);
    } catch (error) {
      console.error('Error normalizing categories:', error);
      alert('Error normalizing categories: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTestTelegram = async () => {
    try {
      const success = await sendTestNotification();
      if (success) {
        alert('✅ Test notification sent! Check your Telegram bot.');
      } else {
        alert('❌ Failed to send notification. Check console for errors.\n\nMake sure you have:\n1. Created a Telegram bot\n2. Added credentials to .env file\n3. Restarted the server\n\nSee TELEGRAM_SETUP.md for instructions.');
      }
    } catch (error) {
      console.error('Error testing Telegram:', error);
      alert('❌ Error: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="loading">Loading admin panel...</div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <h1>Admin Panel</h1>
      
      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          Manage Products
        </button>
        <button 
          className={`tab-btn ${activeTab === 'all-orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('all-orders')}
        >
          All Orders
        </button>
        <button 
          className={`tab-btn ${activeTab === 'custom-orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('custom-orders')}
        >
          Custom Orders
        </button>
        <button 
          className={`tab-btn ${activeTab === 'team' ? 'active' : ''}`}
          onClick={() => setActiveTab('team')}
        >
          Team Management
        </button>
      </div>

      {activeTab === 'products' && (
        <>
          <div className="admin-actions">
            <button onClick={() => setShowForm(true)} className="btn-add-product">
              ➕ Add New Product
            </button>
            <button onClick={handleBulkImport} className="btn-bulk-import" style={{background: '#28a745'}}>
              📦 Import All Products ({productsData.length})
            </button>
            <button onClick={handleNormalizeCategories} className="btn-normalize" style={{background: '#f39c12'}}>
              🔧 Fix Categories
            </button>
            <button onClick={handleTestTelegram} className="btn-test-telegram" style={{background: '#0088cc'}}>
              📱 Test Telegram Bot
            </button>
            <button onClick={handleClearAllProducts} className="btn-clear-all" style={{background: '#dc3545'}}>
              🗑️ Clear All Products
            </button>
          </div>

          {showForm && (
            <div className="form-overlay">
              <div className="form-container">
                <AdminProductForm
                  product={editingProduct}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
              </div>
            </div>
          )}

          <div className="admin-products">
            <h2>Manage Products</h2>
            
            <div className="admin-filters">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="🔍 Search products by name or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                {searchQuery && (
                  <button 
                    className="clear-search"
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
              
              <div className="category-filter">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="category-select-filter"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category === 'All' ? 'All Categories' : category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="products-count">
              Showing {filteredProducts.length} of {products.length} products
              {selectedCategory !== 'All' && ` in ${selectedCategory}`}
              {searchQuery && ` matching "${searchQuery}"`}
            </div>

            <table className="products-table">
              <thead>
                <tr>
                  <th>Available</th>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Price</th>
                  <th>Variants</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{textAlign: 'center', padding: '2rem', color: '#999'}}>
                      {searchQuery || selectedCategory !== 'All' 
                        ? 'No products found matching your filters.' 
                        : 'No products available. Add some products to get started!'}
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map(product => (
                  <tr key={product.id} className={product.available === false ? 'unavailable-row' : ''}>
                    <td>
                      <label className="toggle-switch">
                        <input 
                          type="checkbox" 
                          checked={product.available !== false}
                          onChange={() => handleToggleAvailability(product)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </td>
                    <td><img src={product.image} alt={product.name} className="table-img" /></td>
                    <td>{product.name}</td>
                    <td>
                      <span className={`category-badge ${product.category ? 'has-category' : 'no-category'}`}>
                        {product.category || 'No Category'}
                      </span>
                    </td>
                    <td>{product.description}</td>
                    <td>₹{product.price}{product.category === 'Sweets' ? '/kg' : ''}</td>
                    <td>{product.variants ? product.variants.length : 0}</td>
                    <td>
                      <button onClick={() => handleEdit(product)} className="btn-edit">Edit</button>
                      <button onClick={() => handleDelete(product.id)} className="btn-delete">Delete</button>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'all-orders' && (
        <AllOrdersManagement />
      )}

      {activeTab === 'custom-orders' && (
        <CustomOrderManagement />
      )}

      {activeTab === 'team' && (
        <div className="team-management-redirect">
          <h2>Team Management</h2>
          <p>Manage your team members, add photos, and update team information.</p>
          <button 
            onClick={() => window.location.href = '/team-management'}
            className="btn-team-management"
          >
            Go to Team Management
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
